import {useTheme} from "@emotion/react";
import {useLoaderData} from "@remix-run/react";
import {useRef} from "react";
import Markdown from "react-markdown";
import CTA from "../../../elements/CTA";
import HashtagLink from "../../../elements/hashtagLink/index.jsx";
import {H1} from "../../../elements/semanticWrappers";
import MouseScrollIcon from "../../../elements/svgicon/MouseScroll.jsx";
import {Text} from "../../../elements/text";
import SectionWrapper from "../../../layouts/SectionWrapper";
import {extractSlugAndTypeFromUrl} from "../../../utils/index.js";
import {formatAndLimit} from "../../../utils/text.js";
import {
  GradientBottom,
  GradientTop,
  HashtagsWrapper,
  ScrollToNextSection,
  TextContainer,
} from "./styles.js";

export default function HeroTransparentText({data}) {
  const {translation, language} = useLoaderData();
  const ctaText = translation?.discover;
  const {breakpoints} = useTheme();
  const {isMobile, md} = breakpoints;
  const {title, text, desktop_image, url, id, mobile_image, hashtags} = data;
  const isMobileImg = !!mobile_image?.url;
  const img = isMobile && isMobileImg ? mobile_image?.url : desktop_image?.url;
  const caption =
    isMobile && isMobileImg ? mobile_image?.caption : desktop_image?.caption;
  const firstSectionRef = useRef(null);
  const urlDetails = extractSlugAndTypeFromUrl(url, language);
  const type = urlDetails?.type;
  const slug = urlDetails?.slug;

  const scrollToNextSection = () => {
    const sectionHeight = firstSectionRef.current.clientHeight;
    window.scrollTo({top: sectionHeight, behavior: "smooth"});
  };

  const mobileText = formatAndLimit(text, 125);
  const mobileTitle = formatAndLimit(title, 62);

  return (
    <div ref={firstSectionRef} style={{position: "relative"}}>
      <SectionWrapper
        id={id}
        bgImage={img}
        styles={{
          height: isMobile ? 667 : 810,
          paddingInline: 0,
          marginBottom: isMobile ? 0 : 40,
        }}
        innerWrapperStyles={{
          height: "100%",
          minHeight: isMobile ? 667 : "auto",
          gridColumnGap: isMobile ? 0 : 10,
        }}
      >
        <GradientTop />
        <GradientBottom />
        <TextContainer>
          <HashtagsWrapper>
            {!!hashtags.length &&
              hashtags?.map((hashtag) => (
                <HashtagLink
                  styles={{color: "white"}}
                  key={hashtag.term_id}
                  to={`#${hashtag.slug}`}
                >
                  {hashtag.name}
                </HashtagLink>
              ))}
          </HashtagsWrapper>
          <H1
            styles={{
              color: "white",
              maxWidth: md ? "100%" : "70%",
              marginBlock: isMobile ? "7px 0px" : "15px 20px",
              fontSize: md ? "2.125rem" : "3.5rem",
              lineHeight: md ? "2.5rem" : "4rem",
            }}
          >
            <Markdown>
              {isMobile
                ? `${mobileTitle}${mobileTitle.length === 62 ? "..." : ""}`
                : title}
            </Markdown>
          </H1>
          <Text
            styles={{
              marginTop: isMobile ? 30 : "auto",
              marginBottom: 40,
              color: "white",
              lineHeight: isMobile ? 1.3 : "1.75rem",
              maxWidth: isMobile ? "100%" : 896,
            }}
            type="textLarge"
          >
            {isMobile ? `${mobileText}${mobileText.length === 125 ? "..." : ""}` : text}
          </Text>
          {type && slug && (
            <CTA
              buttonStyles={{
                alignSelf: "start",
                marginBottom: caption ? 0 : isMobile ? 50 : 57,
              }}
              size="medium"
              type="primary"
              to={`${type}/${slug}`}
            >
              {ctaText}
            </CTA>
          )}

          {!!caption && (
            <Text
              styles={{
                marginTop: isMobile ? 30 : 57,
                color: "rgba(255, 255, 255, 0.8)",
                whiteSpace: "nowrap",
                textAlign: "end",
              }}
              type="textSmallRegular"
            >
              {caption}
            </Text>
          )}
        </TextContainer>

        {!isMobile && (
          <ScrollToNextSection onClick={(e) => scrollToNextSection(e)}>
            <MouseScrollIcon />
          </ScrollToNextSection>
        )}
      </SectionWrapper>
    </div>
  );
}
